import Breadcrumb from '@/components/base/Breadcrumb';
import { useSeo } from '@/hooks/useSeo';
import ContactForm from './components/ContactForm';

const ABOUT_IMAGE =
  'https://readdy.ai/api/search-image?query=warm%20modern%20creative%20studio%20illustration%2C%20diverse%20young%20team%20working%20with%20laptops%20drawing%20tablets%20and%20comic%20storyboards%2C%20pudding%20desserts%20on%20table%2C%20caramel%20cream%20orange%20color%20palette%2C%20clean%20flat%20vector%20illustration%2C%20cozy%20friendly%20atmosphere%2C%20no%20text&width=1600&height=900&seq=about-studio-2026&orientation=landscape';

const stats = [
  { value: '2019', label: '成立年份' },
  { value: '1,200+', label: '收录作品' },
  { value: '300万+', label: '月活跃读者' },
  { value: '60+', label: '日均更新话数' },
];

const testimonials = [
  {
    name: '木子同学',
    role: '追更《寄宿日记》2 年的读者',
    text: '追《寄宿日记》两年了，更新稳定还免费，画质清晰没有水印，必须给布丁漫画网点赞！',
  },
  {
    name: '熬夜看漫人',
    role: '资深漫画爱好者',
    text: '界面干净清爽，没有乱七八糟的广告弹窗，阅读体验比很多大平台都要好，推荐给身边的朋友了。',
  },
  {
    name: '小满不吃香菜',
    role: '手机端用户',
    text: '分类做得特别全，搜索也快，手机浏览器直接看就很流畅，不用下载 App 太省事了。',
  },
];

const honors = ['读者口碑推荐奖', '年度最佳免费漫画平台', '超 1200 部正版收录'];

export default function About() {
  useSeo({
    title: '关于我们-布丁漫画网',
    description:
      '布丁漫画网成立于 2019 年，是一家专注于漫画在线免费阅读的中文平台，收录《寄宿日记》等 1200+ 部作品，坚持免费、流畅、更新及时的阅读体验。',
    keywords: '布丁漫画网,关于我们,漫画平台,在线免费阅读,寄宿日记',
    path: '/about',
    ogImage: ABOUT_IMAGE,
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: '关于布丁漫画网',
        description:
          '布丁漫画网是专注于漫画在线免费阅读的中文平台，收录千余部作品，坚持免费、流畅、更新及时的阅读体验。',
        inLanguage: 'zh-CN',
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: '首页',
            item: `${window.location.origin}${__BASE_PATH__ || ''}/`,
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: '关于我们',
            item: `${window.location.origin}${__BASE_PATH__ || ''}/about`,
          },
        ],
      },
    ],
  });

  return (
    <div>
      <section className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '关于我们' }]} />
        <div className="mt-4 rounded-2xl bg-background-100 p-6 md:p-8">
          <h1 className="text-2xl text-foreground-950 md:text-3xl">关于布丁漫画网</h1>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-foreground-600 md:text-base">
            布丁漫画网成立于 2019 年，是一家专注于中文漫画在线免费阅读的平台。
            我们以《寄宿日记》等热门作品为起点，逐步收录恋爱、校园、日常、治愈、
            奇幻、热血等全题材漫画，坚持"免费阅读、流畅体验、每日更新"三大承诺，
            让每一位漫画爱好者都能轻松找到并畅读心仪的作品。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-12 md:px-6 md:pb-16">
        <div className="grid grid-cols-1 items-center gap-8 lg:grid-cols-2">
          <div className="overflow-hidden rounded-2xl">
            <div className="aspect-[16/9] w-full">
              <img
                src={ABOUT_IMAGE}
                alt="布丁漫画网创作团队办公场景插画"
                title="关于布丁漫画网创作团队"
                className="h-full w-full object-cover object-center"
              />
            </div>
          </div>
          <div>
            <h2 className="text-xl text-foreground-950 md:text-2xl">我们是谁</h2>
            <p className="mt-4 text-sm leading-7 text-foreground-600">
              我们是一群热爱漫画的年轻人，来自技术、设计、内容等不同领域。
              因为共同的爱好走到一起，决定做一件简单却有意义的事：
              让好漫画不被付费墙挡住，让每个人都能随时随地享受阅读的快乐。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              从选品、翻译校对到排版上线，每一话内容都经过编辑团队严格把关，
              力求画面清晰、更新及时、体验流畅。你的每一次阅读，都是我们坚持下去的动力。
            </p>
            <ul className="mt-5 flex flex-col gap-2.5">
              {[
                { icon: 'ri-heart-3-fill', text: '坚持免费：全站作品永久免费在线阅读' },
                { icon: 'ri-flashlight-fill', text: '更新及时：热门连载第一时间同步' },
                { icon: 'ri-device-fill', text: '体验流畅：多端适配，打开即读' },
              ].map((item) => (
                <li key={item.text} className="flex items-center gap-2.5 text-sm text-foreground-700">
                  <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                    <i className={item.icon}></i>
                  </span>
                  {item.text}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
          {stats.map((item) => (
            <div key={item.label} className="rounded-2xl bg-background-100 p-5 text-center">
              <span className="font-heading text-2xl text-primary-600 md:text-3xl">{item.value}</span>
              <span className="mt-1 block text-xs text-foreground-500 md:text-sm">{item.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-secondary-50">
        <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
          <div className="text-center">
            <h2 className="text-xl text-foreground-950 md:text-2xl">读者口碑</h2>
            <p className="mt-2 text-sm text-foreground-600">
              感谢每一位读者的信任与陪伴
            </p>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((item) => (
              <figure key={item.name} className="rounded-2xl bg-background-50 p-6">
                <div className="flex items-center gap-1 text-accent-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={`star-${i}`} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <blockquote className="mt-4 text-sm leading-7 text-foreground-700">
                  “{item.text}”
                </blockquote>
                <figcaption className="mt-4 border-t border-background-200 pt-3">
                  <p className="text-sm font-semibold text-foreground-900">{item.name}</p>
                  <p className="mt-0.5 text-xs text-foreground-500">{item.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            {honors.map((item) => (
              <span
                key={item}
                className="flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-50 px-5 py-2 text-sm text-secondary-900"
              >
                <i className="ri-award-fill text-primary-500"></i>
                {item}
              </span>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <div className="rounded-2xl bg-background-100 p-6 md:p-8">
          <h2 className="text-xl text-foreground-950 md:text-2xl">版权与正版声明</h2>
          <p className="mt-4 text-sm leading-7 text-foreground-600">
            布丁漫画网尊重并保护知识产权。本站收录内容均来自公开渠道整理，
            仅用于学习与交流展示；如您是作品的版权方，认为相关内容侵犯了您的权益，
            欢迎通过下方联系方式与我们沟通，我们将在核实后第一时间处理下架。
            同时，我们呼吁广大读者支持正版漫画，为创作者加油。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-12 md:px-6 md:pb-16">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <div className="rounded-2xl bg-background-100 p-6 md:p-8">
            <h2 className="text-xl text-foreground-950 md:text-2xl">联系我们</h2>
            <p className="mt-3 text-sm leading-6 text-foreground-600">
              无论是意见反馈、合作洽谈还是内容举报，我们都欢迎您的来信。
            </p>
            <ul className="mt-6 flex flex-col gap-4 text-sm text-foreground-700">
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-100 text-primary-600">
                  <i className="ri-mail-fill"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">官方邮箱</p>
                  <p className="font-medium">contact@pudingmh.com</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary-100 text-secondary-700">
                  <i className="ri-wechat-fill"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">微信公众号</p>
                  <p className="font-medium">布丁漫画</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-100 text-accent-600">
                  <i className="ri-map-pin-2-fill"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">办公地址</p>
                  <p className="font-medium">中国 · 杭州 · 文一西路 100 号创意园</p>
                </div>
              </li>
              <li className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-background-200 text-foreground-600">
                  <i className="ri-time-line"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">在线时间</p>
                  <p className="font-medium">每日 9:00 - 22:00</p>
                </div>
              </li>
            </ul>
          </div>
          <ContactForm />
        </div>
      </section>
    </div>
  );
}